/**
 * Chemistry lab — reaction review.
 *
 * Every mix a student tries that is not already approved is computed by the
 * model and parked as `pending_review`, with a count of how many times it has
 * been tried since. Approving one means a person has read the chemistry and is
 * willing to have it shown to students verbatim, with no model call behind it.
 *
 * Ordered by how often each mix has actually been tried, because that is the
 * order that matters: a reaction fourteen students hit is worth a minute of
 * someone's attention long before one that was tried once.
 */
import { useCallback, useEffect, useState } from 'react';
import { chemistryLabApi, type LabReaction, type ReactionStatus } from '../api/chemistryLabApi';
import './ChemistryLabReview.css';

const TABS: { key: ReactionStatus; label: string }[] = [
  { key: 'pending_review', label: 'Needs review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

/** The outcome fields worth showing as chips, in the order a reader wants them. */
const FIELDS: { key: string; label: (v: unknown) => string }[] = [
  { key: 'equation', label: v => String(v) },
  { key: 'colorChange', label: v => { const c = v as { from?: string; to?: string }; return `colour: ${c.from} → ${c.to}`; } },
  { key: 'gasEvolution', label: v => { const g = v as { gas?: string; description?: string }; return `gas: ${g.gas}${g.description ? ` (${g.description})` : ''}`; } },
  { key: 'precipitate', label: v => { const p = v as { amount?: string; color?: string }; return `precipitate: ${p.amount}, ${p.color}`; } },
  { key: 'temperatureChange', label: v => `temperature: ${(v as { direction?: string }).direction}` },
  { key: 'litmusResult', label: v => `litmus: ${v}` },
  { key: 'pHBand', label: v => `pH: ${String(v).replace(/_/g, ' ')}` },
  { key: 'boilingPointC', label: v => `boils at ${v} °C` },
];

export default function ChemistryLabReview() {
  const [tab, setTab] = useState<ReactionStatus>('pending_review');
  const [rows, setRows] = useState<LabReaction[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (status: ReactionStatus) => {
    setLoading(true);
    setError(null);
    try {
      const data = await chemistryLabApi.listReactions(status);
      setRows(data.reactions);
      setCounts(data.counts);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the review queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(tab); }, [tab, load]);

  async function review(mixKey: string, status: ReactionStatus) {
    setBusy(mixKey);
    setError(null);
    try {
      await chemistryLabApi.review(mixKey, status);
      // Drop it from the list it just left, and keep the tab counts honest
      // without a refetch.
      setRows(prev => prev.filter(r => r.mixKey !== mixKey));
      setCounts(prev => ({ ...prev, [tab]: (prev[tab] ?? 1) - 1, [status]: (prev[status] ?? 0) + 1 }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="clr">
      <header className="clr-head">
        <h1>Chemistry lab reactions</h1>
        <p className="clr-sub">
          What students actually mixed, and what the model said happened. Approving one
          serves it to everyone who tries that mix, with no model call.
        </p>
      </header>

      <nav className="clr-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`clr-tab${tab === t.key ? ' is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {counts[t.key] !== undefined && <span className="clr-count">{counts[t.key]}</span>}
          </button>
        ))}
      </nav>

      {error && <p className="clr-error">{error}</p>}
      {loading && <p className="clr-empty">Loading…</p>}
      {!loading && rows.length === 0 && (
        <p className="clr-empty">
          {tab === 'pending_review' ? 'Nothing waiting. Every mix students have tried has been looked at.' : 'Nothing here yet.'}
        </p>
      )}

      <ul className="clr-list">
        {rows.map(r => (
          <li key={r.mixKey} className="clr-row">
            <div className="clr-row-head">
              <span className="clr-mix">{r.substanceIds.join(' + ')}</span>
              <span className="clr-band">{r.temperatureBand}</span>
              <span className="clr-hits" title="Times students have tried this mix">{r.hitCount}×</span>
            </div>

            {r.outcome.narrationText && <p className="clr-narration">{String(r.outcome.narrationText)}</p>}

            <div className="clr-chips">
              {FIELDS.filter(f => r.outcome[f.key] !== undefined && r.outcome[f.key] !== null).map(f => (
                <span key={f.key} className="clr-chip">{f.label(r.outcome[f.key])}</span>
              ))}
              {r.outcome.confidence === 'predicted' && (
                <span className="clr-chip clr-chip-warn" title="The model was not confident about this one">predicted</span>
              )}
            </div>

            <div className="clr-foot">
              <span className="clr-meta">
                {r.source === 'seed' ? 'seeded' : 'from the model'}
                {r.reviewedBy && ` · reviewed by ${r.reviewedBy}`}
                {r.modelRunId && ` · run ${r.modelRunId}`}
              </span>
              <span className="clr-actions">
                {tab !== 'approved' && (
                  <button className="clr-btn clr-approve" disabled={busy === r.mixKey} onClick={() => review(r.mixKey, 'approved')}>
                    Approve
                  </button>
                )}
                {tab !== 'rejected' && (
                  <button className="clr-btn" disabled={busy === r.mixKey} onClick={() => review(r.mixKey, 'rejected')}>
                    Reject
                  </button>
                )}
                {tab !== 'pending_review' && (
                  <button className="clr-btn" disabled={busy === r.mixKey} onClick={() => review(r.mixKey, 'pending_review')}>
                    Send back
                  </button>
                )}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
